import { routes } from "../routes";
import { hasBrandExclude } from "./brand-queries";
import { pathWithProject } from "./project-id";

export type KeywordMapDestination = "detail" | "list" | "create" | "brand-setup";

export type KeywordMapSummary = {
  status?: string;
};

// A project stores a single keyword map. Once that map is finished, Keyword Map
// opens it directly. Until then the list shows preparation status.
export function shouldOpenFinishedKeywordMap(
  maps: KeywordMapSummary[] | null | undefined
): boolean {
  return (
    Array.isArray(maps) &&
    maps.length > 0 &&
    maps.every((map) => map?.status === "finished")
  );
}

// A finished map opens directly. No map yet opens the create form. A map that
// is still being prepared opens the list. A stored brand pattern opens the
// create form. Otherwise the project edit form is where the brand pattern is set.
export function keywordMapDestination(
  project: {
    key_map?: string;
    brand_queries?: string[];
  } | null | undefined,
  maps?: KeywordMapSummary[] | null
): KeywordMapDestination {
  if (project?.key_map === "yes") {
    if (shouldOpenFinishedKeywordMap(maps)) {
      return "detail";
    }
    if (Array.isArray(maps) && maps.length === 0) {
      return "create";
    }
    return "list";
  }
  if (hasBrandExclude(project?.brand_queries)) {
    return "create";
  }
  return "brand-setup";
}

export function keywordMapPath(
  project: {
    id?: string;
    key_map?: string;
    brand_queries?: string[];
  } | null | undefined,
  maps?: KeywordMapSummary[] | null
): string {
  const destination = keywordMapDestination(project, maps);
  if (destination === "detail") {
    return pathWithProject(routes.DetailKeywordMapWizard.path, project?.id);
  }
  if (destination === "list") {
    return pathWithProject(routes.ListKeywordMapWizard.path, project?.id);
  }
  if (destination === "create") {
    return pathWithProject(routes.createKeywordMap.path, project?.id);
  }
  const editPath = pathWithProject(routes.editProject.path, project?.id);
  if (editPath === routes.dashboard.path) {
    return editPath;
  }
  return editPath + "#brand_queries";
}

/** Project edit page, opened on the Keyword Map section where tags are defined. */
export function keywordMapTagSettingsPath(projectId: string | null | undefined): string | null {
  const editPath = pathWithProject(routes.editProject.path, projectId);
  if (editPath === routes.dashboard.path) {
    return null;
  }
  return editPath + "?section=keyword";
}
